import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  LinearProgress,
  Checkbox,
  Rating,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  Chat as ChatIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Pill, SectionHeader } from '../components/shared';
import { SAGE, CREAM, WARM } from '../theme';
import { API_URL } from '../config';
import useTaskPolling from '../hooks/useTaskPolling';
import useBulletsPolling from '../hooks/useBulletsPolling';
import BulletsPanel from '../components/enhancement/BulletsPanel';
import { mockAuthHeaders } from '../utils/mockAuth';

const ProfileEnhancementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedProjectId = searchParams.get('projectId');
  const linkedProjectTitle = searchParams.get('projectTitle');

  // View state — jump to 'start' if coming from a project link
  const [view, setView] = useState(linkedProjectId ? 'start' : 'hub'); // 'hub', 'start', 'conversation', 'variations'

  // Hub state
  const [conversations, setConversations] = useState([]);
  const [hubStats, setHubStats] = useState({ total_conversations: 0, completed: 0, in_progress: 0 });
  const [hubLoading, setHubLoading] = useState(true);

  // Conversation state
  const [topic, setTopic] = useState(linkedProjectTitle ? `Let's talk about my ${linkedProjectTitle} project` : '');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ questions_asked: 0, completion_percentage: 0 });

  // Enhancement state
  const [enhancementId, setEnhancementId] = useState(null);
  const [variations, setVariations] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState(new Set());
  const [ratings, setRatings] = useState({});
  const [error, setError] = useState(null);

  // Track which async operation is in flight for task polling
  const pendingOpRef = useRef(null); // 'start_conversation' | 'send_message' | 'complete_conversation'
  const removedUserMsgRef = useRef(null); // temp user message to remove on failure

  const handleTaskComplete = useCallback((result) => {
    const op = pendingOpRef.current;
    pendingOpRef.current = null;

    if (op === 'start_conversation') {
      setConversationId(result.conversation_id);
      setMessages(result.messages);
      setProgress(result.progress);
      setView('conversation');
    } else if (op === 'send_message') {
      setMessages(result.messages);
      setProgress(result.progress);
      if (result.status === 'ready_for_generation') {
        setTimeout(() => handleCompleteConversation(), 1000);
      }
    } else if (op === 'complete_conversation') {
      setEnhancementId(result.enhancement_id);
      setVariations(result.variations);
      setView('variations');
    }
    setIsProcessing(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTaskError = useCallback((taskError) => {
    const op = pendingOpRef.current;
    pendingOpRef.current = null;
    setIsProcessing(false);

    if (op === 'start_conversation') {
      setError('Failed to start conversation. Please try again.');
    } else if (op === 'send_message') {
      setError('Failed to send message.');
      // Remove the optimistic user message
      if (removedUserMsgRef.current) {
        setMessages(prev => prev.filter(m => m.id !== removedUserMsgRef.current));
        removedUserMsgRef.current = null;
      }
    } else if (op === 'complete_conversation') {
      setError('Failed to generate variations.');
    }
  }, []);

  const { startTask: startPolling, progress: taskProgress } = useTaskPolling({
    onComplete: handleTaskComplete,
    onError: handleTaskError,
    interval: 2000,
  });

  // Bullets polling — independent of conversation messages
  const {
    bullets, bulletsStatus, isGenerating: bulletsGenerating, triggerPoll: triggerBulletsPoll, setBullets,
  } = useBulletsPolling(conversationId, {
    enabled: !!conversationId && view === 'conversation',
  });

  const bulletsProgress = React.useMemo(() => {
    const strongBullets = bullets.filter(bullet => {
      const total = Object.values(bullet.scores || {}).reduce((a, b) => a + b, 0);
      return total >= 12 && bullet.goalAlignment?.evidenceStrength === 'strong';
    });
    return { current: strongBullets.length, target: 5 };
  }, [bullets]);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    setHubLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/profile/enhancement/conversations`, {
        headers: { ...mockAuthHeaders() },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        setHubStats(data.stats || { total_conversations: 0, completed: 0, in_progress: 0 });
      }
    } catch (err) {
      console.error('[ENHANCEMENT] Error loading conversations:', err);
    } finally {
      setHubLoading(false);
    }
  };

  // Resume an existing conversation
  const handleResumeConversation = async (convId) => {
    setIsProcessing(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/profile/enhancement/conversations/${convId}`,
        {
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to load conversation');
      const data = await response.json();
      setConversationId(convId);
      setTopic(data.topic);
      setMessages(data.messages);
      setProgress(data.progress);
      if (data.bullets) setBullets(data.bullets);

      if (data.status === 'completed') {
        // Go to enhancement flow for completed conversations
        navigate(`/enhancement?conversationId=${convId}`);
      } else {
        setView('conversation');
      }
    } catch (err) {
      console.error('[ENHANCEMENT] Error resuming:', err);
      setError('Failed to load conversation.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Start Conversation (async task)
  const handleStartConversation = async () => {
    if (!topic.trim()) return;
    setIsProcessing(true);
    setError(null);
    pendingOpRef.current = 'start_conversation';
    try {
      const response = await fetch(`${API_URL}/api/profile/enhancement/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ topic: topic.trim(), project_id: linkedProjectId || undefined }),
      });
      if (!response.ok) throw new Error('Failed to start conversation');
      const data = await response.json();
      startPolling(data.task_id);
    } catch (err) {
      console.error('Error starting conversation:', err);
      pendingOpRef.current = null;
      setError('Failed to start conversation. Please try again.');
      setIsProcessing(false);
    }
  };

  // Send Message (synchronous reply via Haiku, bullets generated in background)
  const handleSendMessage = async () => {
    if (!userInput.trim() || !conversationId) return;
    const messageContent = userInput.trim();
    setUserInput('');
    setIsProcessing(true);
    setError(null);

    // Optimistic user message
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await fetch(
        `${API_URL}/api/profile/enhancement/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ content: messageContent }),
        }
      );
      if (!response.ok) throw new Error('Failed to send message');
      const data = await response.json();

      // Update messages and progress from synchronous response
      setMessages(data.messages);
      setProgress(data.progress);

      // Trigger bullets polling (background tasks are generating them)
      triggerBulletsPoll();

      if (data.status === 'ready_for_generation') {
        setTimeout(() => handleCompleteConversation(), 1000);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message.');
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    }
  };

  // Complete Conversation (async task)
  const handleCompleteConversation = async () => {
    if (!conversationId) return;
    setIsProcessing(true);
    setError(null);
    pendingOpRef.current = 'complete_conversation';
    try {
      const response = await fetch(
        `${API_URL}/api/profile/enhancement/conversations/${conversationId}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to generate variations');
      const data = await response.json();
      startPolling(data.task_id);
    } catch (err) {
      console.error('Error generating variations:', err);
      pendingOpRef.current = null;
      setError('Failed to generate variations.');
      setIsProcessing(false);
    }
  };

  // Save Selections
  const handleSaveSelections = async () => {
    if (!enhancementId) return;
    setIsProcessing(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/profile/enhancement/enhancements/${enhancementId}/selections`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({
            selected_variation_ids: Array.from(selectedVariations),
            ratings,
          }),
        }
      );
      if (!response.ok) throw new Error('Failed to save selections');
      const result = await response.json();
      alert(`Added ${result.added_to_profile} bullets to your profile under "${result.highlight_title}".`);

      // Reset and go back to hub
      setView('hub');
      setTopic('');
      setConversationId(null);
      setMessages([]);
      setEnhancementId(null);
      setVariations([]);
      setSelectedVariations(new Set());
      setRatings({});
      loadConversations();
    } catch (err) {
      console.error('Error saving selections:', err);
      setError('Failed to save selections.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // ── Hub View ──
  const renderHubView = () => (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[900] }}
          >
            Conversations
          </Typography>
          <Typography variant="body2" sx={{ color: SAGE[500], mt: 0.5 }}>
            Deep-dive into your experiences to generate powerful resume bullets.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setView('start')}
          sx={{
            bgcolor: SAGE[500],
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': { bgcolor: SAGE[600] },
          }}
        >
          New Conversation
        </Button>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
        {[
          { label: 'Total', value: hubStats.total_conversations },
          { label: 'Completed', value: hubStats.completed },
          { label: 'In Progress', value: hubStats.in_progress },
        ].map(({ label, value }) => (
          <Card key={label} sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '1.5rem', color: SAGE[800] }}>
              {value}
            </Typography>
            <Typography variant="body2" sx={{ color: SAGE[500], fontSize: '0.75rem' }}>
              {label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Conversations List */}
      <Card sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${SAGE[100]}`, bgcolor: CREAM[100] }}>
          <SectionHeader>Recent Conversations</SectionHeader>
        </Box>

        {hubLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: SAGE[400] }} />
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <ChatIcon sx={{ fontSize: 40, color: SAGE[200], mb: 1 }} />
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", color: SAGE[400], fontSize: '0.9rem', mb: 1 }}>
              No conversations yet
            </Typography>
            <Typography variant="body2" sx={{ color: SAGE[400], fontSize: '0.8rem' }}>
              Start a conversation to explore your experiences and generate resume bullets.
            </Typography>
          </Box>
        ) : (
          conversations.map((conv, idx) => (
            <Box
              key={conv.conversation_id}
              onClick={() => handleResumeConversation(conv.conversation_id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                cursor: 'pointer',
                borderBottom: idx < conversations.length - 1 ? `1px solid ${SAGE[50]}` : 'none',
                transition: 'background 0.1s ease',
                '&:hover': { bgcolor: SAGE[50] },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
                <Typography
                  sx={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: SAGE[800],
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {conv.topic}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ color: SAGE[400], fontSize: '0.75rem' }}>
                    {conv.questions_asked || 0} questions
                  </Typography>
                  <Typography variant="body2" sx={{ color: SAGE[300], fontSize: '0.75rem' }}>
                    {formatRelativeDate(conv.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Pill
                  preset={conv.status === 'completed' ? 'sage' : conv.status === 'in_progress' ? 'warm' : 'neutral'}
                  label={conv.status === 'completed' ? 'Completed' : conv.status === 'in_progress' ? 'In Progress' : conv.status}
                />
                <ArrowForwardIcon sx={{ fontSize: 16, color: SAGE[300] }} />
              </Box>
            </Box>
          ))
        )}
      </Card>
    </Box>
  );

  // ── Start View ──
  const renderStartView = () => (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 2 }}>
      <Button
        onClick={() => setView('hub')}
        sx={{ mb: 2, color: SAGE[500], fontFamily: "'Outfit', sans-serif", textTransform: 'none' }}
      >
        Back to Conversations
      </Button>

      <Card sx={{ p: 4 }}>
        <Stack spacing={3} alignItems="center">
          <AutoAwesomeIcon sx={{ fontSize: 48, color: SAGE[500] }} />

          <Typography
            variant="h5"
            sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[900], textAlign: 'center' }}
          >
            New Conversation
          </Typography>

          <Typography variant="body2" sx={{ color: SAGE[600], textAlign: 'center' }}>
            Tell me what you'd like to explore. We'll have a conversation to uncover
            the details that make great resume bullets.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={`Examples:\n- "Let's talk about my projects at Remitly"\n- "I'd like to expand on my time at FlyHomes"\n- "Tell me about the ML infrastructure I built"`}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={isProcessing}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleStartConversation();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontFamily: "'Lora', serif",
                fontSize: '0.9rem',
                '& fieldset': { borderColor: SAGE[200] },
                '&:hover fieldset': { borderColor: SAGE[300] },
              },
            }}
          />

          <Button
            variant="contained"
            size="large"
            startIcon={isProcessing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon />}
            onClick={handleStartConversation}
            disabled={!topic.trim() || isProcessing}
            sx={{
              bgcolor: SAGE[500],
              fontFamily: "'Outfit', sans-serif",
              textTransform: 'none',
              px: 4,
              borderRadius: 2,
              '&:hover': { bgcolor: SAGE[600] },
            }}
          >
            {isProcessing ? 'Starting...' : 'Start Conversation'}
          </Button>

          {/* Task progress while starting */}
          {isProcessing && taskProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: SAGE[500], fontSize: '0.8rem' }}>
                {taskProgress.stage?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                {taskProgress.percent > 0 && ` — ${taskProgress.percent}%`}
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
        </Stack>
      </Card>

      {/* Suggestions */}
      <Box sx={{ mt: 3 }}>
        <SectionHeader sx={{ mb: 1 }}>Need inspiration?</SectionHeader>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[
            "My leadership role at Amazon",
            "Building the fraud detection system",
            "Scaling infrastructure to 1M users",
            "Leading the data migration project",
          ].map((example) => (
            <Pill
              key={example}
              label={example}
              preset="neutral"
              onClick={() => setTopic(example)}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: SAGE[100] } }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  // ── Conversation View (split-panel: chat left, bullets right) ──
  const renderConversationView = () => (
    <Box sx={{ mt: 1 }}>
      <Button
        onClick={() => { setView('hub'); loadConversations(); }}
        sx={{ mb: 1, color: SAGE[500], fontFamily: "'Outfit', sans-serif", textTransform: 'none' }}
      >
        Back to Conversations
      </Button>

      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 140px)' }}>
        {/* Left Panel — Conversation */}
        <Card sx={{ flex: '1 1 55%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${SAGE[100]}`, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: SAGE[800] }}
              >
                Enhancement Conversation
              </Typography>
              <Pill preset="sage" label={`${progress.completion_percentage}% Complete`} />
            </Box>
            <Typography variant="body2" sx={{ color: SAGE[500], mt: 0.5 }}>{topic}</Typography>
            <LinearProgress
              variant="determinate"
              value={progress.completion_percentage}
              sx={{
                mt: 1.5,
                height: 4,
                borderRadius: 2,
                bgcolor: SAGE[100],
                '& .MuiLinearProgress-bar': { bgcolor: SAGE[500], borderRadius: 2 },
              }}
            />
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Stack spacing={2}>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: message.role === 'user' ? SAGE[500] : CREAM[200],
                      color: message.role === 'user' ? '#fff' : SAGE[800],
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {message.content}
                    </Typography>
                    {message.extracted_facts?.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${message.role === 'user' ? 'rgba(255,255,255,0.2)' : SAGE[200]}` }}>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.8 }}>
                          Extracted:
                        </Typography>
                        {message.extracted_facts.slice(0, 3).map((fact, idx) => (
                          <Typography key={idx} variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                            {fact}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: SAGE[400], ml: 1, mt: 0.5, fontSize: '0.65rem' }}>
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Stack>
          </Box>

          {/* Input */}
          <Box sx={{ p: 2, borderTop: `1px solid ${SAGE[100]}`, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type your response..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                disabled={isProcessing}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: "'Lora', serif",
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: SAGE[200] },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!userInput.trim() || isProcessing}
                sx={{
                  minWidth: 90,
                  bgcolor: SAGE[500],
                  textTransform: 'none',
                  fontFamily: "'Outfit', sans-serif",
                  borderRadius: 2,
                  '&:hover': { bgcolor: SAGE[600] },
                }}
              >
                {isProcessing ? '...' : 'Send'}
                {!isProcessing && <SendIcon sx={{ ml: 0.5, fontSize: 18 }} />}
              </Button>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
          </Box>
        </Card>

        {/* Right Panel — Bullets */}
        <Card sx={{ flex: '1 1 45%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {bulletsGenerating ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb', bgcolor: 'white', flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 1, fontFamily: "'Outfit', sans-serif", fontSize: '1rem' }}>
                  Generated Bullets
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Updating based on your conversation...
                </Typography>
              </Box>
              {bullets.length > 0 ? (
                <Box sx={{ flex: 1, opacity: 0.6 }}>
                  <BulletsPanel bullets={bullets} progress={bulletsProgress} onSaveBullet={() => {}} />
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={24} sx={{ color: SAGE[400], mb: 2 }} />
                    <Typography variant="body2" sx={{ color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
                      Generating bullets...
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <BulletsPanel bullets={bullets} progress={bulletsProgress} onSaveBullet={() => {}} />
          )}
        </Card>
      </Box>
    </Box>
  );

  // ── Variations View ──
  const renderVariationsView = () => (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 2 }}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography
          sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1.2rem', color: SAGE[900], mb: 0.5 }}
        >
          {variations.length} Variations Generated
        </Typography>
        <Typography variant="body2" sx={{ color: SAGE[600], mb: 2 }}>
          Select your favorites — each variation emphasizes different aspects of your work.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Pill preset="sage" label={`${selectedVariations.size} selected`} />
          <Pill preset="neutral" label={`${Object.keys(ratings).length} rated`} />
        </Box>
      </Card>

      <Stack spacing={2}>
        {variations.map((variation) => {
          const isSelected = selectedVariations.has(variation.id);
          return (
            <Card
              key={variation.id}
              sx={{
                p: 0,
                borderColor: isSelected ? SAGE[400] : undefined,
                bgcolor: isSelected ? SAGE[50] : undefined,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      const s = new Set(selectedVariations);
                      e.target.checked ? s.add(variation.id) : s.delete(variation.id);
                      setSelectedVariations(s);
                    }}
                    icon={<CheckCircleIcon sx={{ color: SAGE[200] }} />}
                    checkedIcon={<CheckCircleIcon sx={{ color: SAGE[500] }} />}
                  />

                  <Box sx={{ flex: 1 }}>
                    <Pill preset="sage" label={variation.emphasis} sx={{ mb: 1 }} />

                    <Typography variant="body1" sx={{ color: SAGE[800], mb: 1.5, lineHeight: 1.6 }}>
                      {variation.text}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {variation.best_for_roles?.map(role => (
                        <Pill key={role} preset="warm" label={role} />
                      ))}
                    </Box>

                    {variation.keywords?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {variation.keywords.map(kw => (
                          <Pill key={kw} preset="neutral" label={kw} />
                        ))}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: SAGE[500] }}>
                        {variation.word_count} words
                      </Typography>
                      <Typography variant="caption" sx={{ color: SAGE[500] }}>
                        ATS: {variation.ats_friendly_score}/10
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                        <Typography variant="caption" sx={{ color: SAGE[500] }}>Rate:</Typography>
                        <Rating
                          value={ratings[variation.id] || 0}
                          onChange={(e, v) => setRatings({ ...ratings, [variation.id]: v })}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>
          );
        })}
      </Stack>

      {/* Sticky Actions */}
      <Card sx={{ p: 2.5, mt: 3, position: 'sticky', bottom: 16 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => { setView('hub'); loadConversations(); }}
            sx={{ color: SAGE[500], textTransform: 'none', fontFamily: "'Outfit', sans-serif" }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSelections}
            disabled={selectedVariations.size === 0 || isProcessing}
            startIcon={<CheckCircleIcon />}
            sx={{
              bgcolor: SAGE[500],
              textTransform: 'none',
              fontFamily: "'Outfit', sans-serif",
              borderRadius: 2,
              '&:hover': { bgcolor: SAGE[600] },
            }}
          >
            {isProcessing ? 'Saving...' : `Save Selected (${selectedVariations.size})`}
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Card>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {view === 'hub' && renderHubView()}
      {view === 'start' && renderStartView()}
      {view === 'conversation' && renderConversationView()}
      {view === 'variations' && renderVariationsView()}
    </Box>
  );
};

export default ProfileEnhancementPage;
